import { TRPCClientError } from '@trpc/client';

export type BackendHealth = 'healthy' | 'down';

/**
 * True when an error plausibly means "the backend is unreachable or failing
 * wholesale" rather than a request-specific failure. 4xx responses are the
 * request's own problem; a 5xx, or a tRPC error that never carried response
 * data (network failure, gateway error page), is grounds to go confirm via
 * /api/health before alarming the user — this alone never shows the overlay.
 */
export function isBackendUnavailableError(error: unknown): boolean {
  if (!(error instanceof TRPCClientError)) {
    return false;
  }
  const data: unknown = error.data;
  if (data === null || data === undefined) {
    return true;
  }
  if (typeof data !== 'object' || !('httpStatus' in data)) {
    return false;
  }
  const { httpStatus } = data;
  return typeof httpStatus === 'number' && httpStatus >= 500;
}

/**
 * Probe the backend's health endpoint. Reports 'down' only for the
 * signatures an outage actually produces — 503 (backend up, database
 * unreachable, e.g. during Cloud SQL maintenance), 502/504 (gateway cannot
 * reach the backend), or a failed/timed-out fetch. Any other status,
 * including an unexpected one like 404 from an older backend build, reads
 * as 'healthy' so a deploy mismatch can never pin the maintenance overlay up.
 */
export async function fetchBackendHealth(fetchImpl: typeof fetch = fetch): Promise<BackendHealth> {
  try {
    // AbortSignal.timeout is feature-detected: on a runtime without it, a
    // synchronous throw here would be caught below and misread as 'down',
    // pinning the overlay. Without the signal the probe just waits on the
    // gateway's own timeout instead.
    const timeout =
      typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(5_000) : undefined;
    const response = await fetchImpl('/api/health', {
      cache: 'no-store',
      ...(timeout === undefined ? {} : { signal: timeout }),
    });
    if (response.status === 503 || response.status === 502 || response.status === 504) {
      return 'down';
    }
    return 'healthy';
  } catch {
    return 'down';
  }
}
